import { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { Form, Button } from 'react-bootstrap';
import { useUserUpdateMyProfileMutation } from '../slices/usersApiSlice';
import { setCredentials } from '../slices/authApiSlice';
import { toast } from 'react-toastify';
import Loader from '../components/Loader';
import { FormPage, FormSection, FormField } from '../components/FormLayout';


export default function MyProfileScreen() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { userInfo } = useSelector((state) => state.auth);

  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    bizName: '',
    primaryEmail: '',
    secondaryEmail: '',
    primaryPhone: '',
    secondaryPhone: '',
    isSubscribedToEmail: false,
    isSubscribedToText: false,
    password: '',
    confirmPassword: ''
  });


  const [updateMyProfile, { isLoading: isUpdating }] =
    useUserUpdateMyProfileMutation();

useEffect(() => { 
  if (userInfo) { 
    const { 
      firstName = '',
      lastName = '',
      bizName = '',
      primaryEmail = '',
      secondaryEmail = '',
      primaryPhone = '',
      secondaryPhone = '',
      isSubscribedToEmail = false,
      isSubscribedToText = false
    } = userInfo;
    
    setFormData((prev) => ({ 
      ...prev,
      firstName,
      lastName,
      bizName,
      primaryEmail,
      secondaryEmail,
      primaryPhone,
      secondaryPhone,
      isSubscribedToEmail,
      isSubscribedToText
    })); 
  } 
}, [userInfo]);

  async function submitHandler(e) {
    e.preventDefault();
    if (formData.password !== formData.confirmPassword) {
      toast.error("Passwords do not match");
    } else {
      try {
        const res = await updateMyProfile({ 
          userId: userInfo._id, 
          ...formData 
        })
        .unwrap();
        dispatch(setCredentials(res));
        toast.success("Success: Your profile has been updated");
        navigate('/');
      } catch (err) {
        toast.error(err?.data?.message || err.error);
      }
    }
  };

  const { search } = useLocation();
  const searchParams = new URLSearchParams(search);
  const redirect = searchParams.get('redirect') || "/";

  // Bind a text input / checkbox to a formData field
  const bindText = (field) => ({
    value: formData[field],
    onChange: (e) => setFormData({ ...formData, [field]: e.target.value }),
  });
  const bindCheck = (field) => ({
    checked: formData[field],
    onChange: (e) => setFormData({ ...formData, [field]: e.target.checked }),
  });

  return (
    <FormPage title='My Profile' subtitle='* Required field'>
      <Form onSubmit={ submitHandler }>
        <FormSection title='Personal'>
          <FormField id='firstName' label='First Name' required half>
            <Form.Control type='text' autoComplete='given-name'
              placeholder='Your first name' {...bindText('firstName')} />
          </FormField>
          <FormField id='lastName' label='Last Name' required half>
            <Form.Control type='text' autoComplete='family-name'
              placeholder='Your last name' {...bindText('lastName')} />
          </FormField>
          <FormField id='bizName' label='Business Name'>
            <Form.Control type='text' autoComplete='organization'
              placeholder='Your business name, if applicable' {...bindText('bizName')} />
          </FormField>
        </FormSection>

        <FormSection title='Contact'>
          <FormField id='primaryEmail' label='Primary E-Mail Address' required half
            help="Used to sign in.">
            <Form.Control type='email' autoComplete='email'
              placeholder='Your primary e-mail address' {...bindText('primaryEmail')} />
          </FormField>
          <FormField id='secondaryEmail' label='Secondary E-Mail Address' half>
            <Form.Control type='email' autoComplete='off'
              placeholder='Your secondary e-mail address' {...bindText('secondaryEmail')} />
          </FormField>
          <FormField id='primaryPhone' label='Primary Phone Number' half>
            <Form.Control type='tel' autoComplete='tel'
              placeholder='Your primary phone number' {...bindText('primaryPhone')} />
          </FormField>
          <FormField id='secondaryPhone' label='Secondary Phone Number' half>
            <Form.Control type='tel' autoComplete='off'
              placeholder='Your secondary phone number' {...bindText('secondaryPhone')} />
          </FormField>
        </FormSection>

        <FormSection title='Communication Preferences'>
          <FormField id='isSubscribedToEmail' half>
            <Form.Check type='checkbox'
              label='Subscribe to promotional e-mail messaging' {...bindCheck('isSubscribedToEmail')} />
          </FormField>
          <FormField id='isSubscribedToText' half>
            <Form.Check type='checkbox'
              label='Subscribe to promotional text messaging' {...bindCheck('isSubscribedToText')} />
          </FormField>
        </FormSection>

        <FormSection title='Change Password'
          description='Leave blank to keep your current password. Your password is used to sign in.'>
          <FormField id='password' label='New Password' half>
            <Form.Control type='password' autoComplete='new-password'
              placeholder='Your new password, if changing it' {...bindText('password')} />
          </FormField>
          <FormField id='confirmPassword' label='Confirm New Password' half>
            <Form.Control type='password' autoComplete='new-password'
              placeholder='Confirm your new password' {...bindText('confirmPassword')} />
          </FormField>
        </FormSection>

        <div className='d-flex justify-content-end gap-2 mt-4 pt-3 border-top'>
          <Link to={redirect} className='btn btn-outline-theme text-decoration-none'>
            Cancel
          </Link>
          <Button type='submit' variant='primary' disabled={isUpdating}>
            Save
          </Button>
        </div>
        {isUpdating && userInfo && <Loader />}
      </Form>
    </FormPage>
  );
};